const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3001';

// Actions safe to replay after reconnect (idempotent / server-validated)
const QUEUEABLE = new Set(["join_room", "leave_room", "set_room_public", "join_spectate"]);
const MAX_QUEUE = 8;

export function createRealtimeClient(onMessage, onStatusChange) {
  let socket = null;
  let reconnectTimer = null;
  let storedToken = null; // persisted so auth is re-sent automatically on reconnect
  let sendQueue = []; // queued actions buffered while disconnected

  function setStatus(status) {
    if (onStatusChange) onStatusChange(status);
  }

  function rawSend(type, payload = {}) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type, payload }));
    }
  }

  function connect() {
    socket = new WebSocket(WS_URL);
    setStatus("connecting");

    socket.addEventListener("open", () => {
      setStatus("connected");
      // Re-authenticate automatically on every (re)connect so the server
      // always has a verified identity for this socket.
      if (storedToken) rawSend("authenticate", { token: storedToken });
      // Flush queued actions (e.g. join_room tapped while briefly offline)
      const queued = sendQueue.splice(0);
      queued.forEach(({ type, payload }) => rawSend(type, payload));
    });

    socket.addEventListener("message", (event) => {
      try {
        onMessage(JSON.parse(event.data));
      } catch (error) {
        console.warn("Invalid realtime message", event.data);
      }
    });

    socket.addEventListener("close", () => {
      setStatus("disconnected");
      reconnectTimer = window.setTimeout(connect, 1500);
    });

    socket.addEventListener("error", () => setStatus("error"));
  }

  connect();

  return {
    send(type, payload = {}) {
      if (socket && socket.readyState === WebSocket.OPEN) {
        rawSend(type, payload);
      } else if (QUEUEABLE.has(type) && sendQueue.length < MAX_QUEUE) {
        // Buffer critical actions; ephemeral ones (roll/move) are intentionally dropped
        sendQueue.push({ type, payload });
      }
    },
    authenticate(token) {
      storedToken = token;
      rawSend("authenticate", { token });
    },
    close() {
      window.clearTimeout(reconnectTimer);
      if (socket) socket.close();
    },
  };
}
